import { gql } from '@apollo/client';
import { Box, Button, chakra, Stack, useDisclosure, useToast, VStack } from '@chakra-ui/react';
import { t } from 'i18next';
import { useMemo } from 'react';
import { RegisterDomainModal } from '~components/organization/SsoTable/RegisterDomainModal';
import { SsoTable } from '~components/organization/SsoTable/SsoTable';
import { useSsoTable } from '~components/organization/SsoTable/useSsoTable';
import { VerticalTabContent } from '~components/ui/VerticalTabs';
import { useConditionalPolling } from '~components/useConditionalPolling';
import {
  OrganizationSsoIndexPageDocument,
  useDomainCreateMutation,
  useOrganizationSsoIndexPageQuery,
} from './__generated__/Index.graphql';

const POLL_INTERVAL = 10000;

export function OrganizationSsoIndexPage() {
  // Fetch actual SSO data from GraphQL with polling
  const { data, loading, error, startPolling, stopPolling } = useOrganizationSsoIndexPageQuery({
    pollInterval: POLL_INTERVAL,
  });

  useConditionalPolling({ startPolling, stopPolling, pollInterval: POLL_INTERVAL });

  // Use actual GraphQL data or empty array
  const ssos = useMemo(() => {
    if (data?.organization?.ssos) {
      return data.organization.ssos;
    }
    return [];
  }, [data]);

  const table = useSsoTable(ssos);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();
  const [createDomain] = useDomainCreateMutation({
    refetchQueries: [{ query: OrganizationSsoIndexPageDocument }],
    awaitRefetchQueries: true,
  });

  const handleRegisterDomain = async (domainUrl: string) => {
    try {
      await createDomain({
        variables: {
          domain: domainUrl,
        },
      });
      toast({
        title: t('domainRegistered'),
        status: 'success',
      });
      onClose();
    } catch (e) {
      const isSsoQuotaExceeded =
        (e as { graphQLErrors?: Array<{ message: string }> })?.graphQLErrors?.[0]?.message ===
        'SSO_QUOTA_EXCEEDED';
      toast({
        title: isSsoQuotaExceeded ? t('ssoQuotaExceededTitle') : t('errorRegisteringDomain'),
        description: isSsoQuotaExceeded ? t('ssoQuotaExceededDescription') : (e as Error).message,
        status: 'error',
      });
    }
  };

  return (
    <VerticalTabContent title={t('sso')} isLoading={loading} error={error}>
      <Stack direction={{ sm: 'column', md: 'row' }} spacing={{ sm: 6, md: 36 }} mb="10">
        <VStack alignItems="stretch" spacing={6} flex="1">
          <chakra.p fontSize="md" color="gray.500">
            {t('ssoDescription')}
          </chakra.p>
        </VStack>
        <Button variant="solid" colorScheme="blue" onClick={onOpen}>
          {t('registerNewDomain')}
        </Button>
      </Stack>

      <RegisterDomainModal isOpen={isOpen} onClose={onClose} onRegister={handleRegisterDomain} />

      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        bgColor="white"
        width={{ base: '500px', md: 'full', lg: 'full' }}
      >
        <SsoTable table={table} />
      </Box>
      <Box display="flex" marginTop="4">
        <Box flex="1">
          <SsoTable.PageSizeSelector table={table} />
        </Box>
        {ssos.length > 0 && <SsoTable.Pagination table={table} />}
      </Box>
    </VerticalTabContent>
  );
}

OrganizationSsoIndexPage.graphql = {
  queries: {
    OrganizationSsoIndexPage: gql`
      query OrganizationSsoIndexPage {
        organization {
          id
          ssos {
            ...SsoTableSso
          }
        }
      }
    `,
  },
  mutations: {
    DomainCreate: gql`
      mutation DomainCreate($domain: String!) {
        ssoDomainCreate(input: { domain: $domain }) {
          id
          domain
        }
      }
    `,
  },
};
